import logger from '../logger';
import { errorDetails } from '../logger/errorDetails';

export type MessageHandler = (message: InboundMessage) => void;
export type StatusHandler = (status: ConnectionStatus) => void;

export type ConnectionStatus = 'connected' | 'disconnected' | 'reconnecting';

export type NotificationPayload = {
  appName: string;
  body: string;
  notId: string;
  title?: string;
  subtitle?: string;
  sound?: string;
  badge?: number;
  category?: string;
  threadId?: string;
  interruptionLevel?: 'passive' | 'active' | 'time-sensitive' | 'critical';
  contentAvailable?: boolean;
  mutableContent?: boolean;
  relevanceScore?: number;
  priority?: 'normal' | 'high';
  summaryText?: string;
  style?: 'inbox' | 'picture' | 'bigText';
  data?: Record<string, string>;
};

export interface ClientOptions {
  url: string;
  environment: string;
  appName: string;
  appVersion: string;
  platform: string;
  deviceId: string;
  getToken: (forceRefresh?: boolean) => Promise<string | undefined>;
  onMessage: MessageHandler;
  onStatus?: StatusHandler;
}

type InitRejectCode =
  | 'INVALID_CONFIGURATION'
  | 'CONNECTION_SUPERSEDED'
  | 'THROTTLED'
  | 'SERVER_UNAVAILABLE';

type InboundMessage =
  | { type: 'init:ack'; connectionId: string; timestamp: number }
  | {
      type: 'init:reject';
      connectionId: string;
      message: string;
      timestamp: number;
      code: InitRejectCode;
      retryAfterMs?: number;
    }
  | { type: 'registerEndpoint:ack'; timestamp: number }
  | {
      type: 'registerEndpoint:reject';
      message: string;
      timestamp: number;
    }
  | { type: 'pong'; timestamp: number }
  | (NotificationPayload & { type: 'notification' });

type OutboundMessage = {
  type: 'registerEndpoint';
  appName: string;
  recipientId: string;
  platform: string;
  endpointArn: string;
};

type ProtocolMessage =
  | {
      type: 'init';
      appName: string;
      appVersion: string;
      platform: string;
      deviceId: string;
    }
  | { type: 'ping' };

export interface WebSocketClient {
  connect: () => void;
  disconnect: () => void;
  restart: () => void;
  dispose: () => void;
  registerEndpoint: (
    recipientId: string,
    platform: string,
    endpointArn: string,
  ) => void;
}

// The timer seams are described by the shape the client uses, not by `typeof
// setTimeout`: the ambient signature also carries a `__promisify__` member,
// which is part of the platform's function object rather than of scheduling a
// callback, and which no injected substitute can satisfy.
// `number` covers the DOM's own return type, which is what the ambient
// `setTimeout` resolves to at runtime in the app.
export type TimerHandle = ReturnType<typeof setTimeout> | number;
type StartTimer = (handler: () => void, timeoutMs: number) => TimerHandle;
type CancelTimer = (handle: TimerHandle) => void;

export interface ClientDeps {
  WebSocketImpl?: typeof WebSocket;
  setTimeoutImpl?: StartTimer;
  clearTimeoutImpl?: CancelTimer;
  random?: () => number;
}

type InternalState = 'idle' | 'connecting' | 'open' | 'blocked' | 'disposed';

const MAX_RECONNECT_DELAY_MS = 30_000;
// Minimum delays the protocol defines for retryable initialisation
// rejections that arrive without a `retryAfterMs` hint.
const RETRY_MINIMUMS: Record<'THROTTLED' | 'SERVER_UNAVAILABLE', number> = {
  THROTTLED: 5000,
  SERVER_UNAVAILABLE: 1000,
};
const HEARTBEAT_MIN_MS = 4 * 60 * 1000;
const HEARTBEAT_SPREAD_MS = 2 * 60 * 1000;

export const createWebSocketClient = (
  opts: ClientOptions,
  deps: ClientDeps = {},
): WebSocketClient => {
  const {
    WebSocketImpl = WebSocket,
    setTimeoutImpl = setTimeout,
    clearTimeoutImpl = clearTimeout,
    random = Math.random,
  } = deps;

  let ws: WebSocket | null = null;
  let heartbeatTimer: TimerHandle | null = null;
  let reconnectTimer: TimerHandle | null = null;
  const pendingMessages: OutboundMessage[] = [];
  let retryAttempt = 0;
  let forceTokenRefresh = false;
  let tokenRefreshUsed = false;
  let active = false;
  let state: InternalState = 'idle';
  // Every connection attempt takes the next generation. Events carrying an
  // older generation belong to a superseded socket and are ignored.
  let generation = 0;
  let initialized = false;
  let pingOutstanding = false;
  const deliveredNotificationIds = new Set<string>();

  const clearReconnect = (): void => {
    if (reconnectTimer !== null) {
      clearTimeoutImpl(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const stopHeartbeat = (): void => {
    if (heartbeatTimer !== null) {
      clearTimeoutImpl(heartbeatTimer);
      heartbeatTimer = null;
    }
    pingOutstanding = false;
  };

  const sendRaw = (payload: OutboundMessage | ProtocolMessage): boolean => {
    if (ws?.readyState === WebSocketImpl.OPEN) {
      try {
        ws.send(JSON.stringify(payload));
        return true;
      } catch (err) {
        logger.warn('Failed to send WebSocket message', {
          err: errorDetails(err),
        });
      }
    }
    return false;
  };

  // Application traffic is gated on `init:ack`: anything sent before the
  // handshake completes is queued rather than written to the socket.
  const sendApplicationMessage = (payload: OutboundMessage): void => {
    if (!initialized || !sendRaw(payload)) pendingMessages.push(payload);
  };

  const flushPendingMessages = (): void => {
    while (
      pendingMessages.length > 0 &&
      ws?.readyState === WebSocketImpl.OPEN
    ) {
      const message = pendingMessages[0];

      try {
        ws.send(JSON.stringify(message));
        pendingMessages.shift();
      } catch (err) {
        logger.warn('Failed to flush pending WebSocket message', {
          err: errorDetails(err),
        });
        return;
      }
    }
  };

  const startHeartbeat = (): void => {
    if (!initialized || heartbeatTimer !== null) return;

    const delay =
      HEARTBEAT_MIN_MS + Math.floor(random() * (HEARTBEAT_SPREAD_MS + 1));

    heartbeatTimer = setTimeoutImpl(() => {
      heartbeatTimer = null;
      if (!initialized || ws?.readyState !== WebSocketImpl.OPEN) return;

      // Only one ping may be outstanding: a missing pong means the
      // connection is dead, so drop it and fall into normal backoff.
      if (pingOutstanding) {
        ws.close();
        return;
      }

      pingOutstanding = sendRaw({ type: 'ping' });
      startHeartbeat();
    }, delay);
  };

  const block = (): void => {
    active = false;
    clearReconnect();
    stopHeartbeat();
    state = 'blocked';
    opts.onStatus?.('disconnected');
  };

  const scheduleReconnect = (minimumDelayMs = 0): void => {
    if (
      !active ||
      state === 'blocked' ||
      state === 'disposed' ||
      reconnectTimer !== null
    ) {
      return;
    }

    const cap = Math.min(MAX_RECONNECT_DELAY_MS, 1000 * 2 ** retryAttempt);
    const delay = minimumDelayMs + Math.floor(random() * (cap + 1));
    retryAttempt += 1;
    state = 'idle';
    opts.onStatus?.('reconnecting');

    reconnectTimer = setTimeoutImpl(() => {
      reconnectTimer = null;
      open();
    }, delay);
  };

  const retire = (socket: WebSocket): void => {
    if (ws !== socket) return;

    generation += 1;
    ws = null;
    stopHeartbeat();
    socket.close();
  };

  const handleAuthenticationFailure = (socket?: WebSocket): void => {
    if (socket) retire(socket);

    // One forced credential refresh per authentication failure; a second
    // rejection with fresh credentials is terminal.
    if (tokenRefreshUsed) {
      block();
      return;
    }

    tokenRefreshUsed = true;
    forceTokenRefresh = true;
    scheduleReconnect();
  };

  const handleMessage = (socket: WebSocket, data: string): void => {
    let message: InboundMessage;

    try {
      message = JSON.parse(data);
    } catch {
      logger.error('invalid json', { data });
      return;
    }

    if (ws !== socket) return;

    if (message.type === 'init:ack') {
      retryAttempt = 0;
      tokenRefreshUsed = false;
      forceTokenRefresh = false;
      initialized = true;
      flushPendingMessages();
      startHeartbeat();
      opts.onStatus?.('connected');
      return;
    }

    if (message.type === 'init:reject') {
      retire(socket);

      // `message` is diagnostic only; the code decides the behaviour.
      if (
        message.code === 'THROTTLED' ||
        message.code === 'SERVER_UNAVAILABLE'
      ) {
        scheduleReconnect(message.retryAfterMs ?? RETRY_MINIMUMS[message.code]);
      } else {
        block();
      }
      return;
    }

    if (message.type === 'pong') {
      pingOutstanding = false;
      return;
    }

    if (message.type === 'registerEndpoint:ack') return;

    if (message.type === 'registerEndpoint:reject') {
      logger.error('registerEndpoint rejected', message);
      return;
    }

    if (message.type === 'notification') {
      // The same notification can arrive on more than one initialised
      // connection, so deliver each notId once.
      if (deliveredNotificationIds.has(message.notId)) return;
      deliveredNotificationIds.add(message.notId);
    }

    opts.onMessage(message);
  };

  const open = (): void => {
    if (
      !active ||
      state === 'connecting' ||
      state === 'open' ||
      state === 'blocked' ||
      state === 'disposed'
    ) {
      return;
    }

    // A stable, non-empty device identifier is mandatory: without one the
    // server cannot address this installation.
    if (!opts.deviceId.trim()) {
      logger.error('WebSocket deviceId is missing');
      block();
      return;
    }

    state = 'connecting';
    const attemptGeneration = ++generation;
    const refresh = forceTokenRefresh;
    forceTokenRefresh = false;

    const superseded = (): boolean =>
      attemptGeneration !== generation || !active || state !== 'connecting';

    void opts
      .getToken(refresh)
      .then((token) => {
        if (superseded()) return;

        if (!token) {
          forceTokenRefresh = true;
          scheduleReconnect();
          return;
        }

        const { environment, appName, appVersion, platform, deviceId } = opts;
        const socket = new WebSocketImpl(
          `${opts.url}?environment=${environment}&appName=${appName}&token=${token}`,
        );

        if (superseded()) {
          socket.close();
          return;
        }

        ws = socket;

        socket.onopen = () => {
          if (attemptGeneration !== generation || ws !== socket || !active) {
            return;
          }

          state = 'open';
          initialized = false;

          const sentInit = sendRaw({
            type: 'init',
            appName,
            appVersion,
            platform,
            deviceId,
          });

          if (!sentInit) socket.close();
        };

        socket.onmessage = ({ data }) => handleMessage(socket, data);

        socket.onerror = () => {
          if (attemptGeneration === generation && ws === socket) socket.close();
        };

        // Every close code the gateway can emit is transient: fall into
        // normal backoff unless the client itself stood the socket down.
        socket.onclose = () => {
          if (attemptGeneration !== generation || ws !== socket) return;

          ws = null;
          stopHeartbeat();
          initialized = false;
          state = 'idle';

          if (active) scheduleReconnect();
          else opts.onStatus?.('disconnected');
        };
      })
      .catch((err) => {
        logger.warn('Unable to open WebSocket', { err: errorDetails(err) });
        if (superseded()) return;

        // 400 means the app/environment pair is wrong, so retrying cannot
        // help; 401/403 get one forced credential refresh.
        const status = (err as { status?: number }).status;
        if (status === 400) block();
        else if (status === 401 || status === 403)
          handleAuthenticationFailure();
        else scheduleReconnect();
      });
  };

  const connect = (): void => {
    if (state === 'blocked' || state === 'disposed') return;

    active = true;

    // A pending reconnect already owns the next attempt.
    if (reconnectTimer !== null) return;

    open();
  };

  const disconnect = (): void => {
    active = false;
    clearReconnect();
    stopHeartbeat();
    state = 'idle';
    initialized = false;
    generation += 1;

    const socket = ws;
    ws = null;
    socket?.close();

    opts.onStatus?.('disconnected');
  };

  const restart = (): void => {
    active = true;
    state = 'idle';
    initialized = false;
    retryAttempt = 0;
    tokenRefreshUsed = false;
    forceTokenRefresh = false;
    clearReconnect();
    stopHeartbeat();
    generation += 1;

    const socket = ws;
    ws = null;
    socket?.close();

    open();
  };

  const dispose = (): void => {
    disconnect();
    state = 'disposed';
  };

  const registerEndpoint = (
    recipientId: string,
    platform: string,
    endpointArn: string,
  ): void => {
    sendApplicationMessage({
      type: 'registerEndpoint',
      appName: opts.appName,
      recipientId,
      platform,
      endpointArn,
    });
  };

  return { connect, disconnect, restart, dispose, registerEndpoint };
};
